import React, { use, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemProps } from '@progress/kendo-react-dropdowns';
import { GridColumnState } from '@progress/kendo-react-grid';
import cn from 'clsx';
import styles from './style.module.scss';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import GridMenuItem from '@components-clean/core/grid/grid-menu/grid-menu-item';
import { InfoField } from '@components-clean/core/info-field';
import { TextBox } from '@components-clean/core/text-box';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { DELETE_GRID_STATE, GET_GRID_STATUS, UPSERT_GRID_STATE } from '@queries/common';
import {
    getGridStatus,
    getGridStatusVariables,
    updateGridState,
    updateGridStateVariables,
} from '@queries/generated/common.generated';

export interface GridViewSetting {
    id: string;
    name: string;
    isDefault: boolean;
    selected: boolean;
    gridStateData: string;
    isFavourite: boolean;
    IsDefaultConfig: boolean;
}

interface DeleteGridStateResult {
    deleteGridState: boolean;
}

interface DeleteGridStateVariables {
    id: string;
}

type SavedGridState = {
    columnsState?: GridColumnState[];
};

interface GridStateMenuProps {
    gridId: number;
    currentGridState?: string;
    gridStateVersion?: string;
    onApplyColumnsState?: (state: GridColumnState[]) => void;
    onLayoutChange?: () => void;
    onSettingApplied?: (json: string) => void;
}

const GridStateMenu = ({
    gridId,
    currentGridState,
    gridStateVersion,
    onApplyColumnsState,
    onLayoutChange,
    onSettingApplied,
}: GridStateMenuProps) => {
    const { user } = use(UserContext);
    const { t } = useTranslation('common', { keyPrefix: 'persistentGrids' });
    const { t: tCommon } = useTranslation('common');

    const { data, refetch } = useQuery<getGridStatus, getGridStatusVariables>(GET_GRID_STATUS, {
        variables: { gridId },
        skip: !user?.id,
    });

    const [upsertGridState] = useMutation<updateGridState, updateGridStateVariables>(UPSERT_GRID_STATE, {
        refetchQueries: [GET_GRID_STATUS],
    });

    const [deleteGridState] = useMutation<DeleteGridStateResult, DeleteGridStateVariables>(DELETE_GRID_STATE, {
        refetchQueries: [GET_GRID_STATUS],
    });

    const [viewSettings, setViewSettings] = useState<GridViewSetting[]>([]);
    const [selectedViewSetting, setSelectedViewSetting] = useState<GridViewSetting | null>(null);
    const [showSettingDialog, setShowSettingDialog] = useState(false);
    const [editingSetting, setEditingSetting] = useState<GridViewSetting | null>(null);
    const [newSettingName, setNewSettingName] = useState('');
    const [isFavourite, setIsFavourite] = useState(false);
    const [deletingSetting, setDeletingSetting] = useState<GridViewSetting | null>(null);
    const preserveSelectionIdRef = useRef<string | null>(null);

    const hasChanges =
        gridStateVersion !== undefined &&
        selectedViewSetting !== null &&
        (currentGridState ?? '') !== (selectedViewSetting.gridStateData ?? '');

    const COMPONENT_NAME = 'persistent-grids-edit-layout';

    const hasFormChanges =
        showSettingDialog &&
        (editingSetting
            ? newSettingName.trim() !== editingSetting.name || isFavourite !== editingSetting.isFavourite
            : true);

    useUnsavedChangesTracking({
        area: 'general',
        componentName: COMPONENT_NAME,
        hasChanges: hasFormChanges,
    });

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'general',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    // Save icon: active only for custom (non-default) layouts when there are unsaved changes
    const canSave = hasChanges && !selectedViewSetting?.IsDefaultConfig;
    // Save New icon: active whenever there are unsaved changes, regardless of layout type
    const canSaveNew = hasChanges && viewSettings.length < 11;
    const trimmedNewName = newSettingName.trim().toLowerCase();
    const nameAlreadyExists =
        trimmedNewName.length > 0 &&
        (trimmedNewName === 'default layout' ||
            viewSettings.some((s) => s.name.toLowerCase() === trimmedNewName && s.id !== editingSetting?.id));
    const isSettingNameValid =
        newSettingName.trim().length > 0 && newSettingName.trim().length <= 30 && !nameAlreadyExists;

    const isSubmitEnabled = isSettingNameValid && hasFormChanges;

    const applyViewSetting = (setting: GridViewSetting) => {
        try {
            const parsed = JSON.parse(setting.gridStateData) as SavedGridState;
            onApplyColumnsState?.(parsed.columnsState ?? []);
        } catch {
            onApplyColumnsState?.([]);
        }
        onSettingApplied?.(setting.gridStateData);
    };

    useEffect(() => {
        if (!data?.gridStates) return;
        const settings: GridViewSetting[] = data.gridStates.map((s) => ({
            id: s?.id ?? '',
            name: s?.name ?? '',
            isDefault: s?.isFavourite ?? false,
            selected: false,
            gridStateData: s?.gridStateData ?? '',
            isFavourite: s?.isFavourite ?? false,
            IsDefaultConfig: s?.isDefault ?? false,
        }));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setViewSettings(settings);
        // Apollo can re-run this effect more than once for a single user action (mutation response
        // cache normalization, then a separate refetchQueries-triggered refetch). Don't clear the ref
        // after the first use — leave it "sticky" so every re-run from the same action keeps resolving
        // to the same intended layout, until the next action explicitly re-targets or clears it.
        const settingToSelect: GridViewSetting | null = preserveSelectionIdRef.current
            ? (settings.find((s) => s.id === preserveSelectionIdRef.current) ??
              settings.find((s) => s.isDefault) ??
              settings[0] ??
              null)
            : (settings.find((s) => s.isDefault) ?? settings[0] ?? null);
        setSelectedViewSetting(settingToSelect);
        /* eslint-enable @eslint-react/set-state-in-effect */
        if (settingToSelect) {
            applyViewSetting(settingToSelect);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    const handleSelectViewSetting = (setting: GridViewSetting) => {
        setSelectedViewSetting(setting);
        applyViewSetting(setting);
        // Only clear filter/sort on an explicit layout switch — applyViewSetting is also called to
        // re-affirm the currently selected layout after a save/delete/favourite-toggle refetch, and
        // that shouldn't wipe out whatever filter/sort the user currently has applied.
        onLayoutChange?.();
    };

    const handleSaveViewSetting = () => {
        if (!selectedViewSetting) return;
        preserveSelectionIdRef.current = selectedViewSetting.id;
        upsertGridState({
            variables: {
                gridId,
                gridState: currentGridState ?? '',
                id: selectedViewSetting.id,
                isFavourite: selectedViewSetting.isFavourite,
                name: selectedViewSetting.name,
            },
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.updateGridState) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                await refetch();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const handleAddViewSetting = () => {
        setDeletingSetting(null);
        setEditingSetting(null);
        setNewSettingName('');
        setIsFavourite(false);
        setShowSettingDialog(true);
    };

    const handleCloseSettingDialog = async (changeCheck?: boolean) => {
        if (changeCheck) {
            const confirmed = await confirmLossOfChanges();
            if (!confirmed) return;
        }
        setShowSettingDialog(false);
        setEditingSetting(null);
        setNewSettingName('');
        setIsFavourite(false);
    };

    const handleSubmitSettingDialog = () => {
        const isEditingSelectedSetting = editingSetting?.id === selectedViewSetting?.id;
        if (editingSetting) {
            // Only switch to the edited layout if it was already the one loaded — editing a layout
            // that isn't currently selected shouldn't load it, so keep the current selection instead.
            preserveSelectionIdRef.current = isEditingSelectedSetting
                ? editingSetting.id
                : (selectedViewSetting?.id ?? null);
        }
        upsertGridState({
            variables: {
                gridId,
                // Only capture the live grid state when editing the layout that's actually loaded —
                // otherwise keep the edited layout's own stored gridState untouched.
                gridState:
                    editingSetting && !isEditingSelectedSetting
                        ? editingSetting.gridStateData
                        : (currentGridState ?? ''),
                id: editingSetting?.id,
                isFavourite,
                name: newSettingName.trim(),
            },
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.updateGridState) {
                    ShowError(tCommon('errorMessage'));
                    await handleCloseSettingDialog();
                    return;
                }

                ShowSuccess(tCommon('actionsSuccessfulToast'));
                if (!editingSetting) {
                    const newId = response.data.updateGridState?.id;
                    if (newId != null) preserveSelectionIdRef.current = String(newId);
                }
                await handleCloseSettingDialog();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const handleEditViewSetting = (setting: GridViewSetting) => {
        setDeletingSetting(null);
        setEditingSetting(setting);
        setNewSettingName(setting.name);
        setIsFavourite(setting.isFavourite);
        setShowSettingDialog(true);
    };

    const handleDeleteViewSetting = (setting: GridViewSetting) => {
        setShowSettingDialog(false);
        setEditingSetting(null);
        setNewSettingName('');
        setIsFavourite(false);
        setDeletingSetting(setting);
    };

    const handleCloseDeleteDialog = () => {
        setDeletingSetting(null);
    };

    const handleConfirmDelete = () => {
        if (!deletingSetting) return;
        preserveSelectionIdRef.current =
            deletingSetting.id !== selectedViewSetting?.id && selectedViewSetting ? selectedViewSetting.id : null;
        deleteGridState({ variables: { id: deletingSetting.id } })
            .then(async (response) => {
                if (!response?.data || !response?.data?.deleteGridState) {
                    ShowError(tCommon('errorMessage'));
                    handleCloseDeleteDialog();
                    return;
                }
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                handleCloseDeleteDialog();
                await refetch();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const handleToggleDefaultViewSetting = (setting: GridViewSetting) => {
        preserveSelectionIdRef.current = selectedViewSetting?.id ?? null;
        upsertGridState({
            variables: {
                gridId,
                gridState: setting.gridStateData,
                id: setting.id,
                isFavourite: !setting.isDefault,
                name: setting.name,
            },
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.updateGridState) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                await refetch();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const renderItem = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): React.ReactNode => {
        const setting = itemProps.dataItem as GridViewSetting;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-clone-element
        return React.cloneElement(
            li,
            {},
            <div className={styles.item}>
                <span className={styles.starButton}>
                    <GridMenuItem
                        data-testid="grid-state-menu-default-button"
                        title={setting.isDefault ? t('currentFavorite') : t('setAsFavorite')}
                        onClick={() => {
                            if (!setting.isDefault) handleToggleDefaultViewSetting(setting);
                        }}
                        customIcon={
                            setting.isDefault ? (
                                <FontAwesomeIcon
                                    icon={['fas', 'star']}
                                    style={{ fontSize: '13px' }}
                                    className={styles.starDefault}
                                />
                            ) : (
                                <span className="fa-layers fa-fw">
                                    <FontAwesomeIcon
                                        icon={['far', 'star']}
                                        style={{ fontSize: '13px' }}
                                        className={styles.starOutline}
                                    />
                                    <FontAwesomeIcon
                                        icon={['fas', 'star']}
                                        style={{ fontSize: '13px' }}
                                        className={styles.starSolid}
                                    />
                                </span>
                            )
                        }
                        stopPropagation
                    />
                </span>
                <span className={styles.itemName}>{setting.name}</span>
                {!setting.IsDefaultConfig && (
                    <span className={styles.itemActions}>
                        <GridMenuItem
                            data-testid="grid-state-menu-edit-button"
                            title={t('editLayout')}
                            onClick={() => {
                                handleEditViewSetting(setting);
                            }}
                            icon={['far', 'pen-to-square'] as IconProp}
                            iconFontSize="13px"
                            stopPropagation
                        />
                        <span className={styles.deleteButton}>
                            <GridMenuItem
                                data-testid="grid-state-menu-delete-button"
                                title={t('deleteLayout')}
                                onClick={() => {
                                    handleDeleteViewSetting(setting);
                                }}
                                customIcon={
                                    <FontAwesomeIcon
                                        icon={['fas', 'trash-xmark']}
                                        style={{ fontSize: '13px' }}
                                        className={styles.deleteIcon}
                                    />
                                }
                                stopPropagation
                            />
                        </span>
                    </span>
                )}
            </div>,
        );
    };

    const deleteWarningMessage =
        deletingSetting?.id === selectedViewSetting?.id || deletingSetting?.isFavourite ? (
            <>
                {deletingSetting?.id === selectedViewSetting?.id && <div>{t('layoutCurrentlyUsed')}</div>}
                {deletingSetting?.isFavourite && <div>{t('deleteFavourite')}</div>}
            </>
        ) : null;

    return (
        <>
            <div className={styles.row}>
                <BasicDropDown
                    className={styles.dropdown}
                    data={viewSettings}
                    value={selectedViewSetting}
                    textField="name"
                    dataItemKey="id"
                    data-testid="grid-menu"
                    itemRender={renderItem}
                    onChange={(e) => handleSelectViewSetting(e.value as GridViewSetting)}
                />
                <GridMenuItem
                    data-testid="grid-state-menu-save-button"
                    title={t('saveLayout')}
                    onClick={handleSaveViewSetting}
                    disabled={!canSave}
                    icon={['far', 'floppy-disk'] as IconProp}
                    iconFontSize="21px"
                />
                <GridMenuItem
                    title={t('saveAsNewLayout')}
                    data-testid="grid-state-menu-save-as-new-button"
                    onClick={handleAddViewSetting}
                    customIcon={
                        <span className={cn('fa-layers fa-fw')}>
                            <FontAwesomeIcon icon={['far', 'floppy-disk']} style={{ fontSize: '20px' }} />
                            <FontAwesomeIcon
                                icon={['fas', 'circle']}
                                transform="shrink-5 right-5 down-5"
                                style={{ color: 'white', fontSize: '20px' }}
                            />
                            <FontAwesomeIcon
                                icon={['far', 'plus']}
                                transform="shrink-6 right-5 down-5"
                                style={{ fontSize: '20px' }}
                            />
                        </span>
                    }
                    disabled={!canSaveNew}
                />
                <span className={styles.separator} />
            </div>
            {showSettingDialog && !deletingSetting && (
                <DraggableDialog
                    title={editingSetting ? t('editLayout') : t('saveNewLayout')}
                    dataTestId="layout-dialog"
                    onClose={() => handleCloseSettingDialog(true).catch(() => {})}
                    onSubmit={handleSubmitSettingDialog}
                    primaryBtnLabel={editingSetting ? tCommon('update') : tCommon('save')}
                    primaryBtnDisabled={!isSubmitEnabled}
                    width={426}
                    height={184}
                    setReadOnlyContext
                >
                    <div className="k-w-full">
                        <TextBox
                            data-testid="layout-name"
                            label={t('layoutName')}
                            value={newSettingName}
                            onChange={(e) => setNewSettingName(e.value != null ? String(e.value) : '')}
                            maxLength={30}
                            placeholder={tCommon('enterValue')}
                            valid={!nameAlreadyExists}
                            validationMessage={t('layoutNameAlreadyExists')}
                        />
                        <div>
                            <CoreCheckbox
                                data-testid="setting-favourite"
                                label={t('SetAsMyFavouriteSetting')}
                                value={isFavourite}
                                onChange={(e) => setIsFavourite(e.value ?? false)}
                            />
                        </div>
                    </div>
                </DraggableDialog>
            )}
            {deletingSetting && !showSettingDialog && (
                <DraggableDialog
                    title={t('deleteLayout')}
                    dataTestId="delete-layout-dialog"
                    onClose={handleCloseDeleteDialog}
                    onSubmit={handleConfirmDelete}
                    primaryBtnLabel="Delete"
                    secondaryBtnLabel="Cancel"
                    setReadOnlyContext
                    initialWidth={426}
                    initialHeight={220}
                >
                    <div>
                        {deleteWarningMessage && (
                            <div>
                                <InfoField type="Warning" message={deleteWarningMessage} />
                            </div>
                        )}
                        <p>
                            {t('deleteWarningMessage', {
                                name: deletingSetting.name,
                                interpolation: { escapeValue: false },
                            })}
                        </p>
                    </div>
                </DraggableDialog>
            )}
        </>
    );
};

export default GridStateMenu;
